import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CheckoutGetPurchaseOrderResponse, Cms } from '@impact/data';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { objectIsEmpty } from '../../../../../lib/core/helpers';
import { LEASING_AGREEMENT_TYPES, LeasingService } from '../../../../../lib/leasing/leasing.service';
import { CHECKOUT_STEP, CheckoutFlowService } from '../../../../checkout/checkout-flow.service';
import { CheckoutService } from '../../../../checkout/checkout.service';
import { SettingsService } from '../../../../core/settings.service';

@Component({
    selector: 'app-checkout-step-summary',
    template: `
        <section class="flow-step">
            <ng-container *ngIf="settingsService.get() | async as ss">
                <section *ngIf="!!(checkoutService.isBasketOnBehalf$ | async)" class="flow-step__header">
                    <app-rich-text [html]="ss.checkout.checkoutSummaryStepBasketOnBehalfHeader"></app-rich-text>
                </section>

                <section *ngIf="(checkoutService.isBasketOnBehalf$ | async) === false" class="flow-step__header">
                    <app-rich-text [html]="ss.checkout.checkoutSummeryStepDefaultHeader"></app-rich-text>
                </section>
            </ng-container>

            <ng-container *ngIf="checkoutService.purchaseOrderValidation$ | async as pov">
                <section class="flow-step__header">
                    <app-button
                        buttonClass="button--tertiary button--has-spacing"
                        [title]="'checkout.button____go_back_to_previous_step' | translate"
                        (handleClick)="checkoutFlowService.prevStep()"
                    ></app-button>
                    <app-button
                        (handleClick)="handleSubmit()"
                        [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                        [title]="'checkout.summary_step____button_option____go_to_payment' | translate"
                        [disabled]="!!(checkoutService.purchaseOrderValidationLoading$ | async) || !pov.readyForCompletion"
                    ></app-button>
                </section>

                <section class="flow-step__content flow-step__content--narrow" *ngIf="!pov.readyForCompletion">
                    <div class="state state--alert grid-spacing-large no-margin-horizontally">
                        <p>
                            {{ 'checkout.summary_step____error_message____incomplete_order_information' | translate }}
                        </p>
                    </div>
                </section>
            </ng-container>

            <section class="flow-step__header">
                <h2 class="h4">
                    {{ 'checkout.summary_step____order_overview____headline' | translate }}
                </h2>
                <p>{{ 'checkout.summary_step____order_overview____description' | translate }}</p>
            </section>

            <section class="flow-step__content flow-step__content--narrow"
                [ngSwitch]="isReady && (checkoutService.purchaseOrderValidation$ | async) && (checkoutService.purchaseOrderValidationLoading$ | async) === false">

                <ng-container *ngSwitchCase="true">
                    <app-owner-summary class="animate-fade-in"></app-owner-summary>

                    <div class="summary summary--active" *ngIf="isLeasing" appCircleCheckmark [condition]="true">
                        <div class="summary__header">
                            <h3 class="h5 no-margin">
                                {{leasingDataTexts?.leasingPlanStep?.leasingPlanStepHeader}}
                            </h3>
                            <app-button
                                (click)="checkoutFlowService.goToStep(checkoutSteps.CS_PAYMENT)"
                                [buttonClass]="'button--minimal theme-text'"
                                [title]="'buttons.edit' | translate"
                            ></app-button>
                        </div>
                        <div class="summary__content text-small">
                            <div class="unstyled-table unstyled-table--first-child-bold" role="table">
                                <div class="unstyled-table__row" role="row">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.general?.chosenLeasingPlanText}}
                                    </div>
                                    <div class="unstyled-table__cell"
                                        role="cell"
                                        [ngSwitch]="leasingCheckoutData?.leasingType">
                                        <ng-container *ngSwitchCase="leasingAgreementTypes.OPERATIONAL">
                                            {{ leasingDataTexts?.general?.operationalLeasingText }}
                                        </ng-container>
                                        <ng-container *ngSwitchCase="leasingAgreementTypes.FINANCIAL">
                                            {{ leasingDataTexts?.general?.financialLeasingText }}
                                        </ng-container>
                                    </div>
                                </div>
                                <div class="unstyled-table__row" role="row">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.general?.monthlyPaymentText}}
                                    </div>
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingCheckoutData?.monthlyPayment | defaultcurrency}}
                                    </div>
                                </div>
                                <div class="unstyled-table__row" role="row">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.leasingPlanStep?.firstTimePaymentText}}
                                    </div>
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingCheckoutData?.downPayment | defaultcurrency}}
                                    </div>
                                </div>
                                <div class="unstyled-table__row" role="row">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.leasingPlanStep?.durationOfLoanText}}
                                    </div>
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingCheckoutData?.duration}} {{'generic.month_abbreviation' | translate}}
                                    </div>
                                </div>
                                <div class="unstyled-table__row" role="row">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.general?.kmPerYearText}}
                                    </div>
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingCheckoutData?.kmPerYear | number}} {{leasingDataTexts?.general?.kmPerYearText}}
                                    </div>
                                </div>
                                <div class="unstyled-table__row" role="row" *ngIf="leasingCheckoutData?.residualValue">
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingDataTexts?.general?.residualValueText}}
                                    </div>
                                    <div class="unstyled-table__cell" role="cell">
                                        {{leasingCheckoutData?.residualValue | defaultcurrency}}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <app-product-summary class="animate-fade-in"></app-product-summary>

                    <app-exchange-car-summary class="animate-fade-in"></app-exchange-car-summary>

                    <app-insurance-summary class="animate-fade-in" *ngIf="!isLeasing"></app-insurance-summary>

                    <app-delivery-summary class="animate-fade-in"></app-delivery-summary>

                    <app-aftermarket-equipment class="animate-fade-in" *ngIf="!isLeasing && checkoutData?.equipment?.length"></app-aftermarket-equipment>

                    <app-payment-summary *ngIf="!isLeasing" class="animate-fade-in"></app-payment-summary>

                    <app-price-overview-summary *ngIf="!isLeasing" class="animate-fade-in"></app-price-overview-summary>
                </ng-container>
                <app-component-loader *ngSwitchCase="false"></app-component-loader>
            </section>

            <section class="flow-step__header" *ngIf="checkoutService.purchaseOrderValidation$ | async as pov">
                <app-button
                    buttonClass="button--tertiary button--has-spacing"
                    [title]="'checkout.button____go_back_to_previous_step' | translate"
                    (handleClick)="checkoutFlowService.prevStep()"
                ></app-button>
                <app-button
                    (handleClick)="handleSubmit()"
                    [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                    [title]="'checkout.summary_step____button_option____go_to_payment' | translate"
                    [disabled]="!!(checkoutService.purchaseOrderValidationLoading$ | async) || !pov.readyForCompletion"
                ></app-button>
            </section>
        </section>
    `,
})
export class CheckoutStepSummaryComponent implements OnInit, OnDestroy {
    isReady: boolean;
    isLeasing: boolean;
    leasingDataTexts: Cms.LeasingDataDto['texts'];
    checkoutData: CheckoutGetPurchaseOrderResponse;
    leasingCheckoutData: any;
    leasingAgreementTypes = LEASING_AGREEMENT_TYPES;
    checkoutSteps = CHECKOUT_STEP;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        public leasingService: LeasingService,
        public readonly checkoutService: CheckoutService,
        public readonly checkoutFlowService: CheckoutFlowService,
        public readonly settingsService: SettingsService,
        private cdr: ChangeDetectorRef
    ) {
        this.isReady = false;
        this.isLeasing = false;
        this.checkoutService.validatePurchaseOrder();
    }

    ngOnInit() {
        const checkoutData$ = this.checkoutService.getCheckoutData();
        const leasingGlobalData$ = this.leasingService.getLeasingGlobalData();
        const connectStream$ = combineLatest([checkoutData$, leasingGlobalData$])
        
        connectStream$.pipe(
            map(([checkoutData, leasingGlobalData]) => {

                this.checkoutData = checkoutData;

                if (!objectIsEmpty(checkoutData.lease)) {
                    this.isLeasing = true;
                    this.leasingCheckoutData = checkoutData.lease;
                } else {
                    this.isLeasing = false;
                }

                this.leasingDataTexts = leasingGlobalData?.texts;

                this.isReady = true;
                this.cdr.markForCheck();

            }), takeUntil(this.unsubscribe)).subscribe();
    }

    handleSubmit() {
        this.checkoutService.completePurchaseOrder();
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
